import {isAfter, isEqual, TransportCategory, transportType} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Button, Divider, Grid, NumberInput, Paper, Title} from "@mantine/core";
import {toFormValues, TransportForm, TransportFormType, TransportFormValues} from "../TransportForm/TransportForm";
import {TicketFormProvider} from "./TicketFormProvider/TicketFormProvider";
import {useCallback, useRef} from "react";
import {notifications} from "@mantine/notifications";
import {useForm} from "@mantine/form";
import {Group} from "../../group/Group/Group";

interface TicketFormProps {
  options?: TicketFormOptions;
  defaultValues?: TicketFormDefaultValues;
  onSubmit?: (values: TicketFormValues) => void;
  submitting?: boolean;
}

export interface TicketFormOptions {
  regionId?: string
  category?: TransportCategory
  categoryDisabled?: boolean
  personSelectionDisabled?: boolean;
  siteSelectionDisabled?: boolean;
  wellSelectionDisabled?: boolean;
  containerSelectionDisabled?: boolean;
  pickupHasGeofenceEvent?: boolean
  deliveryHasGeofenceEvent?: boolean
}

export interface TicketFormDefaultValues {
  pickup?: Partial<TransportFormValues>,
  delivery?: Partial<TransportFormValues>,
  manualLoadedMileage?: number
  manualUnloadedMileage?: number
}

export interface TicketFormValues {
  pickup: TransportFormValues
  delivery: TransportFormValues
  manualLoadedMileage?: number
  manualUnloadedMileage?: number
}

export const TicketForm = (props: TicketFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      manualLoadedMileage: defaultValues?.manualLoadedMileage,
      manualUnloadedMileage: defaultValues?.manualUnloadedMileage,
    },
    validate: {}
  });

  const pickupFormRef = useRef<TransportFormType>(undefined)
  const deliveryFormRef = useRef<TransportFormType>(undefined)

  const onFormSubmit = useCallback(() => {
    if (onSubmit) {
      const ttaForm = form
      const pickupForm = pickupFormRef.current
      const deliveryForm = deliveryFormRef.current

      if (ttaForm && pickupForm && deliveryForm) {
        const ttaValidated = !ttaForm.validate().hasErrors
        let pickupValidated = !pickupForm.validate().hasErrors
        let deliveryValidated = !deliveryForm.validate().hasErrors

        const pickupDate = pickupForm.values.date
        const deliveryDate = deliveryForm.values.date

        if (pickupDate && deliveryDate) {
          if (isAfter(pickupDate, deliveryDate)) {
            pickupForm.setFieldError('date', t("common.entity.transporttransportassoc.form.validation.datebefore", "The pickup date can't be before the delivery date"))
            pickupValidated = false
          }

          if (isEqual(pickupDate, deliveryDate)) {
            deliveryForm.setFieldError('date', t("common.entity.transporttransportassoc.form.validation.samedate", "The delivery date must be after the pickup date"))
            deliveryValidated = false
          }
        }

        if (!ttaValidated && !pickupValidated && !deliveryValidated) {
          notifications.show({
            color: "red",
            message: "Fill out all required fields",
          })
        } else if (!pickupValidated) {
          notifications.show({
            color: "red",
            message: "Fill out all required pickup fields",
          })
        } else if (!deliveryValidated) {
          notifications.show({
            color: "red",
            message: "Fill out all required delivery fields",
          })
        } else if (!ttaValidated) {
          notifications.show({
            color: "red",
            message: "Fill out all required metadata fields",
          })
        } else {
          onSubmit({
            pickup: toFormValues(pickupForm.values),
            delivery: toFormValues(deliveryForm.values),
            manualLoadedMileage: form.values.manualLoadedMileage,
            manualUnloadedMileage: form.values.manualUnloadedMileage,
          })
        }
      }
    }
  }, [t, onSubmit, form, pickupFormRef, deliveryFormRef])

  return (
    <TicketFormProvider
      defaultValues={defaultValues}
      pickupFormRef={(form) => pickupFormRef.current = form}
      deliveryFormRef={(form) => deliveryFormRef.current = form}>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >

        <Group position="right">
          <Button onClick={onFormSubmit} loading={submitting}>
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </Group>

        {/* Forms */}
        <Box
          component="main"
          sx={(theme) => ({
            flex: 1,
            minHeight: 0,      // <— very important
            overflowY: "auto",
            padding: theme.spacing.xs,
          })}
        >

          <Grid gutter="md" h="100%">

            {/* Pickup Transport */}
            <Grid.Col span={5} h="100%">
              <Paper
                shadow="sm"
                p="md"
                py="md"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflowY: "scroll",
                }}
              >
                <Title order={4}>
                  {t(transportType.pickup.displayValue.id, transportType.pickup.displayValue.defaultMessage)}
                </Title>
                <Divider orientation="horizontal" my={"lg"}/>
                <TransportForm
                  type={transportType.pickup}
                  options={{
                    regionId: options?.regionId,
                    category: options?.category,
                    categoryDisabled: options?.categoryDisabled,
                    hasGeofenceEvent: options?.pickupHasGeofenceEvent,
                  }}
                  defaultValues={defaultValues?.pickup}/>
              </Paper>
            </Grid.Col>

            {/* Delivery Transport */}
            <Grid.Col span={5} h="100%">
              <Paper
                shadow="sm"
                p="md"
                py="md"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflowY: "scroll",
                }}
              >
                <Title order={4}>
                  {t(transportType.delivery.displayValue.id, transportType.delivery.displayValue.defaultMessage)}
                </Title>
                <Divider orientation="horizontal" my={"lg"}/>
                <TransportForm
                  type={transportType.delivery}
                  options={{
                    regionId: options?.regionId,
                    category: options?.category,
                    categoryDisabled: options?.categoryDisabled,
                    hasGeofenceEvent: options?.deliveryHasGeofenceEvent,
                  }}
                  defaultValues={defaultValues?.delivery}/>
              </Paper>
            </Grid.Col>

            {/* Other Ticket Data */}
            <Grid.Col span={2} h="100%">
              <Paper
                shadow="sm"
                p="md"
                py="md"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflowY: "scroll",
                }}
              >
                <Title order={4}>
                  {t("common.entity.transport.form.section.metadata", "Metadata")}
                </Title>
                <Divider orientation="horizontal" my={"lg"}/>
                <form>
                  <NumberInput
                    size="xs"
                    label={t("common.entity.transport.form.field.manualloadedmileage", "Loaded Mileage (Override)")}
                    placeholder={t<string>("common.entity.transport.form.field.manualloadedmileage.placeholder", "Loaded Mileage (Override)")}
                    precision={5}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    {...form.getInputProps("manualLoadedMileage")} />
                  <NumberInput
                    size="xs"
                    label={t("common.entity.transport.form.field.manualunloadedmileage", "Unloaded Mileage (Override)")}
                    placeholder={t<string>("common.entity.transport.form.field.manualunloadedmileage.placeholder", "Unloaded Mileage (Override)")}
                    precision={5}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    {...form.getInputProps("manualUnloadedMileage")} />
                </form>
              </Paper>
            </Grid.Col>
          </Grid>
        </Box>
      </Box>
    </TicketFormProvider>
  )
};
